/**
 * ResizablePanel Component
 * Resizable and collapsible panel for sidebars
 */

import { useState, useRef, useEffect, type ReactNode } from 'react'
import styles from './ResizablePanel.module.css'

interface ResizablePanelProps {
  side: 'left' | 'right'
  defaultWidth?: number
  minWidth?: number
  maxWidth?: number
  defaultCollapsed?: boolean
  children: ReactNode
  onWidthChange?: (width: number) => void
  onCollapseChange?: (collapsed: boolean) => void
}

export function ResizablePanel({
  side,
  defaultWidth = 300,
  minWidth = 200,
  maxWidth = 600,
  defaultCollapsed = false,
  children,
  onWidthChange,
  onCollapseChange,
}: ResizablePanelProps) {
  const [width, setWidth] = useState(defaultWidth)
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed)
  const [isResizing, setIsResizing] = useState(false)
  const panelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isResizing) return

    const handleMouseMove = (e: MouseEvent) => {
      if (!panelRef.current) return

      const panelRect = panelRef.current.getBoundingClientRect()
      let newWidth: number

      if (side === 'left') {
        newWidth = e.clientX - panelRect.left
      } else {
        newWidth = panelRect.right - e.clientX
      }

      // Constrain width
      newWidth = Math.max(minWidth, Math.min(maxWidth, newWidth))

      setWidth(newWidth)
      onWidthChange?.(newWidth)
    }

    const handleMouseUp = () => {
      setIsResizing(false)
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isResizing, side, minWidth, maxWidth, onWidthChange])

  // 실제 차지하는 폭을 CSS 변수로 노출한다 — 미니맵처럼 캔버스 위에 fixed 로 뜨는
  // 요소가 패널을 가리지 않으려면 리사이즈/접기까지 따라와야 하는데, 폭을 상수로
  // 박아두면 패널을 넓히는 순간 그 위를 덮는다.
  useEffect(() => {
    const prop = `--editor-${side}-panel-width`
    document.documentElement.style.setProperty(prop, isCollapsed ? '0px' : `${width}px`)
    return () => {
      document.documentElement.style.removeProperty(prop)
    }
  }, [side, width, isCollapsed])

  const handleResizeStart = (e: React.MouseEvent) => {
    e.preventDefault()
    setIsResizing(true)
  }

  const toggleCollapse = () => {
    const newCollapsed = !isCollapsed
    setIsCollapsed(newCollapsed)
    onCollapseChange?.(newCollapsed)
  }

  return (
    <>
      {/* Collapse Button - Always visible */}
      <button
        className={`${styles.collapseButton} ${styles[`collapse-${side}`]}`}
        onClick={toggleCollapse}
        style={
          !isCollapsed
            ? {
                [side === 'left' ? 'left' : 'right']: `${width}px`,
              }
            : undefined
        }
        title={isCollapsed ? `Show ${side} panel` : `Hide ${side} panel`}
      >
        {isCollapsed ? (
          side === 'left' ? '→' : '←'
        ) : (
          side === 'left' ? '←' : '→'
        )}
      </button>

      {/* Panel */}
      <div
        ref={panelRef}
        className={`${styles.panel} ${styles[side]} ${isCollapsed ? styles.collapsed : ''}`}
        style={{ width: isCollapsed ? 0 : `${width}px` }}
      >
        {/* Panel Content */}
        {!isCollapsed && <div className={styles.content}>{children}</div>}

        {/* Resize Handle */}
        {!isCollapsed && (
          <div
            className={`${styles.resizeHandle} ${styles[`handle-${side}`]} ${
              isResizing ? styles.resizing : ''
            }`}
            onMouseDown={handleResizeStart}
          />
        )}
      </div>
    </>
  )
}
